import React, { useState } from 'react';
import { Lock, Gift } from 'lucide-react';
import { doc, runTransaction } from 'firebase/firestore';
import { db } from './firebase';
import { Backdrop, Footer, FamilySwitcher } from './Layout';

const NAME_RE = /^[\p{L}\p{N} '’-]{1,30}$/u;
const inputCls = 'w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent';
const pinCls = (err) => `w-full p-3 text-center text-2xl tracking-widest border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${err ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;

// Name + PIN screen for one family: returning members pick a name and enter a
// PIN; new members use "Add me" (no approval needed).
export default function Auth({ code, family, lastMember, onLogin }) {
  const [mode, setMode] = useState('login'); // login | join
  const [selected, setSelected] = useState(family.members.includes(lastMember) ? lastMember : '');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newPin, setNewPin] = useState('');
  const [busy, setBusy] = useState(false);

  const digits = (v) => v.replace(/\D/g, '').slice(0, 4);

  const verify = () => {
    if (family.pins?.[selected] === pin) {
      onLogin(selected);
    } else {
      setError('Incorrect PIN. Try again.');
      setPin('');
    }
  };

  const join = async () => {
    const name = newName.trim().replace(/\s+/g, ' ');
    if (!NAME_RE.test(name)) {
      setError('Use 1–30 letters, numbers, spaces, apostrophes or hyphens.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const ref = doc(db, 'families', code);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('missing');
        const members = snap.data().members || [];
        if (members.some(m => m.toLowerCase() === name.toLowerCase())) {
          throw new Error('duplicate');
        }
        tx.update(ref, { members: [...members, name], [`pins.${name}`]: newPin });
      });
      onLogin(name);
    } catch (e) {
      setError(e.message === 'duplicate'
        ? 'That name is already taken in this family. Pick it from the list instead, or use a different name.'
        : 'Could not add you. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Backdrop>
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center mb-6">
          <FamilySwitcher currentCode={code}>
            {mode === 'login' && selected ? <Lock className="w-12 h-12 text-red-500" /> : <Gift className="w-12 h-12 text-red-500" />}
          </FamilySwitcher>
        </div>
        <h1 className="text-3xl font-bold text-center mb-1 text-gray-800">No Peeking</h1>
        <p className="text-center text-gray-600 mb-6">{family.name}</p>

        {mode === 'login' ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Who are you?</label>
            <select
              className={`${inputCls} mb-4`}
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setPin(''); setError(''); }}
            >
              <option value="">Select your name</option>
              {family.members.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {selected && (
              <>
                <p className="text-center text-gray-600 mb-2">Enter your 4-digit PIN</p>
                <input
                  type="password" inputMode="numeric" maxLength="4" placeholder="____"
                  value={pin} className={pinCls(error)}
                  onChange={(e) => { setPin(digits(e.target.value)); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && pin.length === 4) verify(); }}
                />
                <button
                  onClick={verify} disabled={pin.length !== 4}
                  className="w-full mt-3 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >Enter</button>
              </>
            )}
            {error && <p className="text-red-500 text-sm text-center mt-2">{error}</p>}
            <button
              onClick={() => { setMode('join'); setError(''); }}
              className="w-full mt-6 px-4 py-3 border-2 border-dashed border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >New here? Add me</button>
          </>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your name</label>
            <input
              type="text" maxLength="30" value={newName} placeholder="e.g., Aunt Sue"
              className={`${inputCls} mb-4`}
              onChange={(e) => { setNewName(e.target.value); setError(''); }}
            />
            <label className="block text-sm font-medium text-gray-700 mb-2">Choose a 4-digit PIN</label>
            <input
              type="password" inputMode="numeric" maxLength="4" placeholder="____"
              value={newPin} className={pinCls(false)}
              onChange={(e) => { setNewPin(digits(e.target.value)); setError(''); }}
            />
            <p className="text-xs text-gray-500 text-center mt-2">
              Not a secret vault: anyone with this family's link could look it up. Don't reuse a real PIN.
            </p>
            {error && <p className="text-red-500 text-sm text-center mt-2">{error}</p>}
            <div className="flex gap-2 mt-4">
              <button
                onClick={join} disabled={busy || !newName.trim() || newPin.length !== 4}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >{busy ? 'Adding…' : 'Add me'}</button>
              <button
                onClick={() => { setMode('login'); setError(''); }}
                className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >Back</button>
            </div>
          </>
        )}
      </div>
      <Footer />
    </Backdrop>
  );
}
